/**
 * ProductionBadge — entity data-mode indicator.
 *
 * Four canonical states (per Phase 5 contract):
 *   mock                → "Demo data"        (neutral stone)
 *   production_pending  → "Connecting"       (amber)
 *   production_live     → "Live MYOB data"   (emerald)
 *   production_error    → "Needs attention"  (red)
 *
 * The badge is calm by design: small, pill-shaped, with a tiny coloured dot.
 * It does NOT shout. Use freely — but never duplicate the same badge twice in
 * the same row.
 */

import { CheckCircle2, AlertTriangle, Loader2, Circle } from "lucide-react";

const STATE_META = {
    mock: {
        label: "Demo data",
        cls: "bg-stone-100 text-stone-700 ring-stone-200",
        dotCls: "bg-stone-400",
        Icon: Circle,
    },
    production_pending: {
        label: "Connecting",
        cls: "bg-amber-50 text-amber-800 ring-amber-200",
        dotCls: "bg-amber-500",
        Icon: Loader2,
    },
    production_live: {
        label: "Live MYOB data",
        cls: "bg-emerald-50 text-emerald-800 ring-emerald-200",
        dotCls: "bg-emerald-500",
        Icon: CheckCircle2,
    },
    production_error: {
        label: "Needs attention",
        cls: "bg-red-50 text-red-800 ring-red-200",
        dotCls: "bg-red-500",
        Icon: AlertTriangle,
    },
};

export function ProductionBadge({
    state = "mock",
    size = "sm",
    showIcon = false,
    className = "",
}) {
    const meta = STATE_META[state] || STATE_META.mock;
    const { Icon } = meta;
    const sizeCls = size === "md"
        ? "text-[12px] px-2.5 py-1"
        : "text-[11px] px-2 py-0.5";
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full ring-1 font-medium ${sizeCls} ${meta.cls} ${className}`.trim()}
            data-testid={`production-badge-${state}`}
        >
            {showIcon ? (
                <Icon
                    size={size === "md" ? 12 : 11}
                    strokeWidth={2}
                    className={state === "production_pending" ? "animate-spin" : ""}
                />
            ) : (
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dotCls}`} />
            )}
            {meta.label}
        </span>
    );
}
